import React, { useState } from "react";
import { Autocomplete, GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import Config from "../Helpers/Config";

const containerStyle = { width: "100%", height: "320px", borderRadius: "12px" };
// India-wide fallback view for a brand-new hub with no coordinates yet -
// this platform's phone numbers/currency/GST are all India-specific, so a
// sensible "haven't picked a spot yet" center is India, not (0, 0).
const DEFAULT_CENTER = { lat: 22.3511, lng: 78.6677 };
const DEFAULT_ZOOM = 4.5;
const PICKED_ZOOM = 16;

const addressComponentValue = (components, type) =>
    components?.find((component) => component.types?.includes(type))?.long_name || "";

// Turns a Google `address_components` array into the flat fields the hub
// form actually has - used only when the admin deliberately searches and
// picks a place, not on every marker nudge, so fine-tuning a pin never
// silently overwrites address text someone already typed.
const parseAddressComponents = (components = []) => ({
    street: addressComponentValue(components, "route"),
    area: addressComponentValue(components, "sublocality_level_1")
        || addressComponentValue(components, "sublocality")
        || addressComponentValue(components, "neighborhood"),
    city: addressComponentValue(components, "locality")
        || addressComponentValue(components, "administrative_area_level_2"),
    state: addressComponentValue(components, "administrative_area_level_1"),
    country: addressComponentValue(components, "country"),
    pincode: addressComponentValue(components, "postal_code"),
});

// Search-an-address-or-click/drag-the-map location picker for the Hub form.
// Fully controlled: the parent owns latitude/longitude and gets every
// change back through onLocationChange - this component never keeps its
// own copy of the coordinates, so manual number-entry (still available as
// a fallback if Maps fails to load) and map interaction always agree.
const LocationPicker = ({ latitude, longitude, onLocationChange, onAddressResolved, onPlaceResolved }) => {
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: Config.KEYS.MAP_KEY || "",
        libraries: Config.GOOGLE_MAPS_LIBRARIES,
    });
    const [autocomplete, setAutocomplete] = useState(null);

    const hasLocation = latitude !== "" && latitude !== undefined && latitude !== null
        && longitude !== "" && longitude !== undefined && longitude !== null
        && !Number.isNaN(Number(latitude)) && !Number.isNaN(Number(longitude));
    const center = hasLocation ? { lat: Number(latitude), lng: Number(longitude) } : DEFAULT_CENTER;

    const emitLatLng = (latLng) => {
        onLocationChange?.({ lat: latLng.lat(), lng: latLng.lng() });
    };

    const handleMapClick = (event) => emitLatLng(event.latLng);
    const handleMarkerDragEnd = (event) => emitLatLng(event.latLng);

    const handlePlaceChanged = () => {
        const place = autocomplete?.getPlace();
        if (!place?.geometry?.location) return;
        emitLatLng(place.geometry.location);
        onAddressResolved?.(parseAddressComponents(place.address_components));
        // Raw place (formatted_address, geometry, etc.) for callers that want
        // more than the Hub form's flat street/area/city/state/country/pincode
        // shape - e.g. the delivery/pickup picker, which just wants Google's
        // own formatted_address string. Additive - existing callers that only
        // pass onAddressResolved are unaffected.
        onPlaceResolved?.(place);
    };

    if (!Config.KEYS.MAP_KEY) {
        return (
            <div className="location-picker-fallback">
                Map search isn't configured (missing REACT_APP_MAP_KEY) - enter latitude/longitude manually below.
            </div>
        );
    }

    if (loadError) {
        return <div className="location-picker-fallback">Couldn't load Google Maps - enter latitude/longitude manually below.</div>;
    }

    if (!isLoaded) {
        return <div className="location-picker-fallback">Loading map...</div>;
    }

    return (
        <div className="location-picker">
            <Autocomplete onLoad={setAutocomplete} onPlaceChanged={handlePlaceChanged}>
                <input
                    type="text"
                    className="form-control mb-2"
                    placeholder="Search for an address to jump the map there..."
                />
            </Autocomplete>

            <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={hasLocation ? PICKED_ZOOM : DEFAULT_ZOOM}
                onClick={handleMapClick}
                options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
            >
                {hasLocation ? (
                    <Marker position={center} draggable onDragEnd={handleMarkerDragEnd} />
                ) : null}
            </GoogleMap>

            <div className="form-text">Search an address, or click (or drag the pin) on the map to set the exact location.</div>
        </div>
    );
};

export default LocationPicker;
